import { Alert } from "@mui/material";
import AuthPrimaryButton from "./AuthPrimaryButton";
import AuthTextField from "./AuthTextField";
import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useEnableTotp } from "../../application/hooks/useEnableTotp";
import { useSession } from "../../application/hooks/useSession";
import {
  AUTH_FLOW_PURPOSE_KEY,
  AUTH_FLOW_PURPOSE_PASSWORD_RESET,
  AUTH_FLOW_PURPOSE_RESET_TOTP,
} from "../../domain/constants/authFlowConstants";

interface EmailOtpFormProps {
  purpose: string;
  email: string;
}

const EmailOtpForm = ({ purpose, email }: EmailOtpFormProps) => {
  const { t } = useTranslation("auth");
  const { verifyOtp, resetTotp, loading, error } = useEnableTotp();
  const { getUserEmail } = useSession();
  const navigate = useNavigate();
  const [otp, setOtp] = React.useState("");
  const [localError, setLocalError] = React.useState<string | null>(null);

  const handleVerify = async () => {
    setLocalError(null);

    if (!otp.trim()) {
      setLocalError(t("verifyOtp.otpRequired"));
      return;
    }

    try {
      const userEmail = getUserEmail() || email;

      if (purpose === AUTH_FLOW_PURPOSE_PASSWORD_RESET) {
        await verifyOtp(email, otp.trim(), AUTH_FLOW_PURPOSE_PASSWORD_RESET);
        sessionStorage.setItem(
          AUTH_FLOW_PURPOSE_KEY,
          AUTH_FLOW_PURPOSE_PASSWORD_RESET,
        );
        navigate("/setup-password", {
          state: { email, purpose: AUTH_FLOW_PURPOSE_PASSWORD_RESET },
        });
      } else if (purpose === AUTH_FLOW_PURPOSE_RESET_TOTP) {
        await verifyOtp(email, otp.trim(), AUTH_FLOW_PURPOSE_RESET_TOTP);
        await resetTotp();
        sessionStorage.setItem(
          AUTH_FLOW_PURPOSE_KEY,
          AUTH_FLOW_PURPOSE_RESET_TOTP,
        );
        navigate("/scan-qr");
      } else {
        await verifyOtp(userEmail, otp.trim(), "LOGIN");
        sessionStorage.removeItem(AUTH_FLOW_PURPOSE_KEY);
        navigate("/patients");
      }
    } catch {
      setLocalError(error ?? t("verifyOtp.verifyFailed"));
    }
  };

  const displayError = localError ?? error;

  return (
    <form
      className="flex w-full flex-col gap-6"
      onSubmit={(e) => {
        e.preventDefault();
        void handleVerify();
      }}
    >
      <AuthTextField
        name="otp"
        label={t("verifyOtp.otpLabel")}
        type="text"
        inputMode="numeric"
        placeholder={t("verifyOtp.otpPlaceholder")}
        value={otp}
        onChange={(e) => {
          setOtp(e.target.value);
          setLocalError(null);
        }}
        error={!!displayError}
        helperText={displayError ?? t("verifyOtp.otpHelper")}
        className="w-full"
        disabled={loading}
      />

      <AuthPrimaryButton type="submit" disabled={!otp.trim()} loading={loading}>
        {t("verifyOtp.verify")}
      </AuthPrimaryButton>

      {displayError && (
        <Alert severity="error" sx={{ py: 0 }}>
          {displayError}
        </Alert>
      )}
    </form>
  );
};

export default EmailOtpForm;
